export const DEFAULT_UPLOAD_CONFIG = Object.freeze({
  max_file_size_mb: 5,
  allowed_extensions: [".jpg", ".jpeg", ".png", ".pdf"],
});
const SUPPORTED_UPLOAD_EXTENSIONS = new Set(
  DEFAULT_UPLOAD_CONFIG.allowed_extensions,
);

export const normalizeUploadExtensions = (extensions) => {
  const values = Array.isArray(extensions) && extensions.length
    ? extensions
    : DEFAULT_UPLOAD_CONFIG.allowed_extensions;
  const normalized = [...new Set(values.map((extension) => {
    const normalized = String(extension || "").trim().toLowerCase();
    if (!normalized) return null;
    return normalized.startsWith(".") ? normalized : `.${normalized}`;
  }).filter((extension) => SUPPORTED_UPLOAD_EXTENSIONS.has(extension)))];
  return normalized.length
    ? normalized
    : [...DEFAULT_UPLOAD_CONFIG.allowed_extensions];
};

export const normalizeUploadConfig = (uploadConfig = {}) => {
  const config = uploadConfig && typeof uploadConfig === "object"
    ? uploadConfig
    : {};
  const parsedMax = Number(config.max_file_size_mb);
  return {
    max_file_size_mb: Number.isFinite(parsedMax) && parsedMax > 0
      ? parsedMax
      : DEFAULT_UPLOAD_CONFIG.max_file_size_mb,
    allowed_extensions: normalizeUploadExtensions(config.allowed_extensions),
    upload_type: config.upload_type || "general",
  };
};

export const validateUploadFile = (file, uploadConfig) => {
  const config = normalizeUploadConfig(uploadConfig);
  if (!file) return { valid: false, error: "Please choose a file." };

  const filename = String(file.name || "");
  const extension = filename.includes(".")
    ? `.${filename.split(".").pop().toLowerCase()}`
    : "";
  if (!config.allowed_extensions.includes(extension)) {
    return {
      valid: false,
      error: `Please select a valid file type: ${config.allowed_extensions.join(", ")}`,
    };
  }

  const maxBytes = config.max_file_size_mb * 1024 * 1024;
  if (Number(file.size || 0) > maxBytes) {
    return {
      valid: false,
      error: `File size must be less than ${config.max_file_size_mb} MB.`,
    };
  }
  return { valid: true, config };
};

const bytesStartWith = (bytes, signature) => (
  signature.every((value, index) => bytes[index] === value)
);

export const detectUploadContentType = (input) => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || 0);
  if (bytesStartWith(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d])) {
    return { extension: ".pdf", mime: "application/pdf" };
  }
  if (bytesStartWith(bytes, [0xff, 0xd8, 0xff])) {
    return { extension: ".jpg", mime: "image/jpeg" };
  }
  if (bytesStartWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return { extension: ".png", mime: "image/png" };
  }
  if (
    bytes.length >= 12
    && String.fromCharCode(...bytes.slice(0, 4)) === "RIFF"
    && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP"
  ) {
    return { extension: ".webp", mime: "image/webp" };
  }
  return null;
};

const configuredExtension = (filename) => {
  const value = String(filename || "").toLowerCase();
  return value.includes(".") ? `.${value.split(".").pop()}` : "";
};

const transcodeWebpToConfiguredImage = async (file, targetExtension) => {
  if (
    typeof window === "undefined"
    || typeof window.createImageBitmap !== "function"
    || typeof document === "undefined"
  ) {
    throw new Error(
      "This file contains a WebP image but its filename says PNG or JPG. Please export it as PNG or JPG and try again.",
    );
  }

  const bitmap = await window.createImageBitmap(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image conversion is unavailable in this browser.");

    const targetMime = targetExtension === ".png" ? "image/png" : "image/jpeg";
    if (targetMime === "image/jpeg") {
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(bitmap, 0, 0);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (result) => result
          ? resolve(result)
          : reject(new Error("The selected image could not be converted.")),
        targetMime,
        targetMime === "image/jpeg" ? 0.92 : undefined,
      );
    });
    return new File([blob], file.name, {
      type: targetMime,
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close?.();
  }
};

const readUploadHeader = async (file) => {
  const header = file.slice(0, 16);
  if (typeof header.arrayBuffer === "function") return header.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("The selected file could not be read."));
    reader.readAsArrayBuffer(header);
  });
};

/**
 * Some browsers/download sites save WebP bytes with a PNG/JPG filename. Keep
 * server signature validation strict by re-encoding those images to the
 * configured filename format before upload rather than trusting metadata.
 */
export const prepareUploadFile = async (file) => {
  if (!file?.slice || !file?.name) return file;
  const header = await readUploadHeader(file);
  const detected = detectUploadContentType(header);
  const extension = configuredExtension(file.name);
  if (
    detected?.extension === ".webp"
    && [".png", ".jpg", ".jpeg"].includes(extension)
  ) {
    return transcodeWebpToConfiguredImage(file, extension);
  }
  return file;
};
